import _ from 'lodash'
import Big from 'big.js'
import moment from 'moment'
import * as modelling from '@saxon-trust/facility-modelling'

export default class Facility {
  static facility

  /**
   * Constructor.
   *
   * @param {Object} funding
   */
  constructor (funding) {
    this.funding = funding

    if (this.hasFunding()) {
      this.calculate()
    }
  }

  /**
   * Run facility calculations.
   */
  calculate () {
    this.facility = new modelling.Facility()
    this.facility.calculate(this.funding)
  }

  /**
   * Get the modelled facility.
   */
  getFacility () {
    return this.facility
  }

  /**
   * Get the funding.
   */
  getFunding () {
    return this.funding
  }

  /**
   * Get the formatted type text.
   *
   * @param {String} type
   * @returns {String}
   */
  getType (type) {
    switch (type) {
      case 'Draw':
        return 'Loan'
      case 'InterestPay':
        return 'Interest Paid'
      default:
        return type
    }
  }

  /**
   * Get the amount for the view,
   */
  getViewAmount (view) {
    return view[`amountLoan${view.loanIndex + 1}`].toFixed(2)
  }

  /**
   * Get description for the view.
   */
  getViewDescription (view) {
    switch (view.description) {
      case 'Interest Loan1':
        return 'Interest (1st)'
      case 'Interest Loan2':
        return 'Interest (2nd)'
      case 'Interest Pay1':
        return 'Interest (1st) Servicing'
      case 'Interest Pay2':
        return 'Interest (2nd) Servicing'
      default:
        return view.description
    }
  }

  /**
   * Get type for the view.
   *
   * @deprecated
   */
  getViewType (view) {
    return this.getType(view.type)
  }

  /**
   * We have valid funding.
   */
  hasFunding () {
    return this.funding && Object.keys(this.funding).length !== 0
  }

  /**
   * Amount was debited.
   */
  isDebited (view) {
    return ['InterestPay'].includes(view.type) && view[`amountLoan${view.loanIndex + 1}`].gt(0.00)
  }

  /**
   * Get the projected schedule.
   */
  projectedSchedule () {
    return this.getFacility().schedule
  }

  /**
   * Get the projected summary.
   */
  projectedSummary () {
    return this.getFacility().summary
  }

  /**
   * Get projected daily interest.
   */
  projectedDailyInterest () {
    return this.facility.getProjectedDailyInterest()
  }

  /**
   * Get projected monthly interest.
   */
  projectedMonthlyInterest () {
    const dailyInterest = this.projectedDailyInterest()
    const projected = []

    for (let loan of dailyInterest) {
      let index = dailyInterest.indexOf(loan)
      let months = {}

      for (let day of loan) {
        let month = moment(day.date).format('YYYY/MM')

        if (months[month] === undefined) {
          months[month] = {
            amount: 0,
            date: day.date
          }
        }

        months[month].amount = new Big(months[month].amount).plus(day.amount)
      }

      projected[index] = months
    }

    return projected
  }

  /**
   * Get projected monthly statement.
   */
  projectedMonthlyStatement () {
    const months = {}

    this.projectedSchedule().forEach(day => {
      const date = moment(day.date)
      const month = date.format('YYYYMM')
      const actions = _.get(day, 'view')

      if (_.isArray(actions)) {
        if (!_.has(months, month)) {
          _.set(months, month, { date, statements: [] })
        }

        actions.forEach(action => {
          const { balanceDrawn, interestOwed, loanIndex, type } = action
          const number = loanIndex + 1
          const amount = _.get(action, `amountLoan${number}`)
          const statements = _.get(months, `${month}.statements`)

          statements.push({
            date: date.format('DD/MM/YYYY'),
            type: this.getType(type),
            amount: amount.toFixed(2),
            balance: balanceDrawn.toFixed(2),
            interest: interestOwed.toFixed(2),
            debit: this.isDebited(action)
          })

          _.set(months, `${month}.statements`, statements)
        })
      }
    })

    // return statements
    return months
  }

  /**
   * Get projected yearly interest.
   */
  projectedYearlyInterest () {
    const dailyInterest = this.projectedDailyInterest()
    const projected = []

    for (let loan of dailyInterest) {
      let index = dailyInterest.indexOf(loan)
      let years = {}

      for (let day of loan) {
        let year = moment(day.date).format('YYYY')

        if (years[year] === undefined) {
          years[year] = {
            amount: 0,
            date: day.date
          }
        }

        years[year].amount = new Big(years[year].amount).plus(day.amount)
      }

      projected[index] = years
    }

    return projected
  }

  /**
   * Get the total fees.
   */
  totalFees () {
    return this.projectedSummary().totalFees.toFixed(2)
  }

  /**
   * Get total headroom at term.
   */
  totalHeadroomAtTerm () {
    return 0.00
  }

  /**
   * Get total loan.
   */
  totalLoan () {
    return 0.00
  }

  /**
   * Get the total interest accrued.
   */
  totalInterestAccrued () {
    return this.projectedSummary().totalInterestAccrued.toFixed(2)
  }

  /**
   * Get the total interest charged.
   */
  totalInterestCharged () {
    return this.projectedSummary().totalInterestCharged.toFixed(2)
  }

  /**
   * Get the total interest paid.
   */
  totalInterestPaid () {
    return this.projectedSummary().totalInterestPaid.toFixed(2)
  }

  /**
   * Get total returns
   */
  totalReturns () {
    return 0.00
  }

  /**
   * Get total revenue.
   */
  totalRevenue () {
    const { totalToBeRepaid } = this.projectedSummary()
    const { amount } = this.getFunding()

    return totalToBeRepaid.minus(amount).toFixed(2)
  }

  /**
   * Get the total to be repaid.
   */
  totalToBeRepaid () {
    return this.projectedSummary().totalToBeRepaid.toFixed(2)
  }

  /**
   * Aggregate in loan.
   */
  aggregateInLoan (loan, property = 'amount') {
    if (loan === undefined) {
      return 0.00
    }

    let aggregated = new Big(0)
    let attribute

    for (let row of loan) {
      attribute = _.get(row, property)
      aggregated = aggregated.plus(attribute.toFixed(2))
    }

    return aggregated.toFixed(2)
  }
}
